import React, { useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { isProjectsInJiraM2Enabled } from '@jira/platform__services-native-integration-projects/src/index.tsx';
import { useProjectsAnalyticsL2 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l2/index.tsx';
import { ENTITY_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { ProjectActionMenu } from '@jira/navigation-apps__project-actions-action-menu/src/ui/ProjectActionMenu.tsx';
import type { JiraLinkConfig } from '@jira/platform__routing-router-components/src/ui/router-link-config/index.tsx';
import { useProjectMenuItemDragAndDrop } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-customise-order/src/controllers/useProjectMenuItemDragAndDrop.tsx';
import { ContainerAvatar as Avatar } from '@atlaskit/side-nav-items/container-avatar';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import type { JiraBusinessProjectMenu$key } from './__generated__/JiraBusinessProjectMenu.graphql';
import { JiraBusinessProjectExpandableMenu } from './JiraBusinessProjectExpandableMenu.tsx';

type JiraBusinessProjectMenuProps = {
	projectData: JiraBusinessProjectMenu$key;
	isCorePremiumUserSeat?: boolean;
	menuItemAnchorRef?: React.RefObject<HTMLAnchorElement>;
};

export const JiraBusinessProjectMenu = ({
	projectData,
	isCorePremiumUserSeat = false,
	menuItemAnchorRef,
}: JiraBusinessProjectMenuProps): React.JSX.Element => {
	const { sendMeatBallDropDownOnProjectsAnalyticsEvent } = useProjectsAnalyticsL2();
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming
	const project = useFragment<JiraBusinessProjectMenu$key>(
		graphql`
			fragment JiraBusinessProjectMenu on JiraProject
			@argumentDefinitions(
				isSubspaceEnabled: {
					type: "Boolean!"
					provider: "@jira/platform__graphql-relay-provider/src/atlas-project-native-integration-m2.relayprovider"
				}
			) {
				avatar @required(action: THROW) {
					large @required(action: THROW)
				}
				name @required(action: THROW)
				projectKey: key @required(action: THROW)
				projectId @required(action: THROW)
				projectType @required(action: THROW)
				isFavourite
				defaultNavigationItem {
					... on JiraNavigationItem {
						url
					}
				}
				subspaceCount: subspaceCount_cse @include(if: $isSubspaceEnabled)
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		projectData,
	);

	let isTargetSameAsCurrentLocation = false;
	if (typeof window !== 'undefined') {
		isTargetSameAsCurrentLocation = window.location.pathname === project.defaultNavigationItem?.url;
	}

	const projectUrl = project.defaultNavigationItem?.url ?? `/browse/${project.projectKey}`;

	const routerLinkConfig: JiraLinkConfig = useMemo(
		() => ({
			to: projectUrl,
			prefetch: isTargetSameAsCurrentLocation ? false : 'hover',
		}),
		// oxlint-disable-next-line react/react-compiler
		[isTargetSameAsCurrentLocation, projectUrl],
	);

	const projectActionMenuOnClickHandler = useCallback(() => {
		const isFavourite = project.isFavourite;
		const projectId = project.projectId;
		const projectType = project.projectType;

		sendMeatBallDropDownOnProjectsAnalyticsEvent({
			// TODO BLU-5544 send section via <ContextualAnalyticsData instead (which wraps the Starred or Recent panel; and each project). Use static object or useMemo, not to have rerendering issues. Then you can also use static object for meatBallDropDownOnProjectsAnalytics. See Jsm and Wanjel components
			starred: isFavourite ?? false,
			recent: !isFavourite,
			projectId,
			projectType,
		});
	}, [
		project.isFavourite,
		project.projectId,
		project.projectType,
		sendMeatBallDropDownOnProjectsAnalyticsEvent,
	]);

	// Memoize the inline JSX passed to the menu item. Without this, `elemBefore`
	// and `actionsOnHover` are fresh React elements on every render, so even when
	// their content is identical they force the menu-item subtree
	// (IconButton/button-base/pressable) to re-render on every parent render —
	// the SSR→boot re-render waste measured via Chippy.
	const elemBefore = useMemo(
		() => <Avatar src={project.avatar.large || ''} />,
		[project.avatar.large],
	);
	const actionsOnHover = useMemo(
		() => (
			<ProjectActionMenu
				isProjectMenuReorderingEnabled={Boolean(project.isFavourite)}
				projectKey={project.projectKey}
				isCorePremiumUserSeat={isCorePremiumUserSeat}
				onClick={projectActionMenuOnClickHandler}
				projectName={project.name}
			/>
		),
		[
			project.isFavourite,
			project.projectKey,
			project.name,
			isCorePremiumUserSeat,
			projectActionMenuOnClickHandler,
		],
	);

	const { projectId, projectKey, isFavourite, name, avatar } = project;
	const { state, draggableAnchorRef, dropTargetRef, dropIndicator, dragPreview, hasDragIndicator } =
		useProjectMenuItemDragAndDrop({
			projectId,
			projectKey,
			isFavourite,
			name,
			avatar,
			isEnabled: !!isFavourite,
		});

	if (project.subspaceCount && project.subspaceCount > 0 && isProjectsInJiraM2Enabled()) {
		return (
			<>
				<JiraBusinessProjectExpandableMenu<JiraLinkConfig>
					ref={mergeRefs(menuItemAnchorRef || null, draggableAnchorRef)}
					visualContentRef={dropTargetRef}
					dropIndicator={dropIndicator}
					isDragging={state.type === 'dragging'}
					hasDragIndicator={hasDragIndicator}
					elemBefore={elemBefore}
					href={routerLinkConfig}
					// oxlint-disable-next-line react/react-compiler
					menuId={ENTITY_ID.PROJECT(project.projectKey)}
					projectKey={project.projectKey}
					projectUrl={projectUrl}
					actionsOnHover={actionsOnHover}
				>
					{project.name}
				</JiraBusinessProjectExpandableMenu>
				{dragPreview}
			</>
		);
	}

	return (
		<>
			<Nav4MenuLinkItem<JiraLinkConfig>
				ref={mergeRefs(menuItemAnchorRef || null, draggableAnchorRef)}
				visualContentRef={dropTargetRef}
				dropIndicator={dropIndicator}
				isDragging={state.type === 'dragging'}
				hasDragIndicator={hasDragIndicator}
				elemBefore={elemBefore}
				href={routerLinkConfig}
				// oxlint-disable-next-line react/react-compiler
				menuId={ENTITY_ID.PROJECT(project.projectKey)}
				actionsOnHover={actionsOnHover}
			>
				{project.name}
			</Nav4MenuLinkItem>
			{dragPreview}
		</>
	);
};
